import { forwardRef, RefObject } from 'react';

import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Portal,
  Spinner,
  Stack,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  ArrowRightOutline,
  DropdownInput,
  DropdownOption,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';

import useFetchCompanies from 'app/hooks/useFetchCompanies';

import { useCreateEmployeeStore } from 'app/store';

import { employeeCompanyFormSchema } from 'app/utils/schemas';

import { EmployeeFormProps, EmployeeCompanyFormFields } from '../types';

const EmployeeCompanyForm = forwardRef<HTMLElement, EmployeeFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const employeeData = useCreateEmployeeStore((state) => state.employeeData);
    const setEmployeeData = useCreateEmployeeStore((state) => state.setEmployeeData);

    const {
      control,
      handleSubmit,
      formState: { errors, isValid },
    } = useForm<EmployeeCompanyFormFields>({
      mode: 'onBlur',
      defaultValues: employeeData as EmployeeCompanyFormFields,
      resolver: yupResolver(employeeCompanyFormSchema),
    });

    const handleNextStep = (data: EmployeeCompanyFormFields) => {
      setEmployeeData(data);
      onStepAction();
    };

    const { data: companies, isLoading: isLoadingCompanies } = useFetchCompanies({
      page: 0,
      size: 999999,
      sortBy: 'name',
      sort: 'ASC',
    });

    const companyOptions: DropdownOption[] =
      companies?.list.map((company) => ({
        value: company.id,
        text: company.name,
      })) || [];

    const handleBackCLick = () => {
      previousStep();
    };

    return isLoadingCompanies ? (
      <Center width="full" height="full" flexGrow={1}>
        <Spinner color="brand.purple" emptyColor="brand.purpleAlpha30" size="xl" />
      </Center>
    ) : (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          <FormControl isInvalid={!!errors?.companyId?.message} isRequired>
            <FormLabel>Select a company</FormLabel>
            <Controller
              control={control}
              name="companyId"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="Which company are you creating the employees for?"
                  defaultValue={companyOptions.find((company) => company.value === value)}
                  options={companyOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                  isSearchable
                  searchPlaceholder="Search company..."
                />
              )}
            />
            <FormErrorMessage>{errors?.companyId?.message}</FormErrorMessage>
          </FormControl>

          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

EmployeeCompanyForm.displayName = 'EmployeeCompanyForm';

export default EmployeeCompanyForm;
